import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Pricing = () => {
  const { t } = useTranslation();
  const [isMonthly, setIsMonthly] = useState(true);

  const toggleBilling = () => {
    setIsMonthly(!isMonthly);
  };

  // Price display based on current billing period
  const monthlyPrice = '9,99€';
  const annualPrice = '4,17€';
  const currentPrice = isMonthly ? monthlyPrice : annualPrice;
  const billingPeriod = isMonthly ? t('pricing.perMonth') : t('pricing.perMonth');

  return (
    <section id="pricing" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-primary mb-4">{t('pricing.title')}</h2>
            <p className="text-xl text-primary mb-8">{t('pricing.subtitle')}</p>
            
            {/* Billing toggle */}
            <div className="flex justify-center items-center space-x-4 mb-8">
              <span className={`text-sm font-medium ${isMonthly ? 'text-secondary' : 'text-gray-400'}`}>
                {t('pricing.monthly')}
              </span>
              
              <button
                onClick={toggleBilling}
                className="relative inline-flex h-6 w-12 items-center rounded-full bg-dark-gray transition-colors focus:outline-none"
                aria-pressed={!isMonthly}
              >
                <span className="sr-only">
                  {isMonthly ? t('pricing.switchToAnnual') : t('pricing.switchToMonthly')}
                </span>
                <span
                  className={`${
                    isMonthly ? 'translate-x-1' : 'translate-x-7'
                  } inline-block h-4 w-4 transform rounded-full bg-secondary transition-transform`}
                />
              </button>
              
              <span className={`text-sm font-medium ${!isMonthly ? 'text-secondary' : 'text-gray-400'}`}>
                {t('pricing.annual')}
                <span className="ml-1 text-xs bg-secondary text-background rounded-full px-2 py-0.5">
                  {t('pricing.savePercent')}
                </span>
              </span>
            </div>
          </div>
          
          {/* Pricing cards */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Free plan */}
            <div className="bg-dark-gray rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-secondary/20 border border-gray-800">
              <div className="p-8">
                <h3 className="text-2xl font-bold text-primary mb-4">{t('pricing.freePlan.title')}</h3>
                <p className="text-gray-400 mb-6">{t('pricing.freePlan.description')}</p>
                
                <div className="mb-8">
                  <span className="text-primary text-4xl font-bold">0€</span>
                  <span className="text-gray-400 ml-2">{t('pricing.forever')}</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {[1, 2, 3, 4].map((i) => (
                    <li key={i} className="flex items-start">
                      {i === 1 ? (
                        // Check mark (green) for feature 1
                        <svg className="h-6 w-6 text-secondary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        // X mark (red) for features 2-4
                        <svg className="h-6 w-6 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className="ml-3 text-primary">{t(`pricing.freePlan.feature${i}`)}</span>
                    </li>
                  ))}
                </ul>
                
                <a 
                  href="https://https://forms.gle/yfzs4ENsg2JwPLQPA"
                  target="_blank"
                  rel="noopener noreferrer" 
                  className="block w-full py-3 px-8 rounded-full bg-dark-gray text-primary font-medium border border-secondary hover:bg-secondary hover:text-background transition-colors duration-300 text-center"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'click_dicci_la_tua', {
                        'event_category': 'engagement',
                        'event_label': 'pricing_free_plan',
                        'language': document.documentElement.lang || 'it',
                        'page_location': window.location.pathname
                      });
                      console.log('GA4 Event Tracked:', 'click_dicci_la_tua', {
                        label: 'pricing_free_plan',
                        language: document.documentElement.lang || 'it',
                        page: window.location.pathname
                      });
                    }
                  }}
                >
                  {t('pricing.freePlan.cta')}
                </a>
              </div>
            </div>
            
            {/* Premium plan */}
            <div className="bg-dark-gray rounded-2xl overflow-hidden relative transition-all duration-300 hover:shadow-lg hover:shadow-secondary/20 border border-gray-800">
                {/* Popular badge - only show for annual plan */}
              {!isMonthly && (
                <div className="absolute top-0 right-0">
                  <div className="bg-secondary text-background text-xs uppercase font-bold py-1 px-3 rounded-bl-lg">
                    {t('pricing.recommended')}
                  </div>
                </div>
              )}
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-primary mb-4">{t('pricing.premiumPlan.title')}</h3>
                <p className="text-gray-400 mb-6">{t('pricing.premiumPlan.description')}</p>
                
                {/* Price with highlight for annual plan */}
                <div className="mb-8">
                  <div className="flex items-center">
                    <span className="text-primary text-4xl font-bold">{currentPrice}</span>
                    <span className="text-gray-400 ml-2">{billingPeriod}</span>
                    
                    {/* Discount info moved to the right side of price */}
                    {!isMonthly && (
                      <div className="flex items-center ml-2">
                        <span className="text-gray-400 text-xs">{t('pricing.annualBilling')}</span>
                        <span className="mx-1 text-gray-500">•</span>
                        <div className="bg-secondary text-background text-xs font-bold py-0.5 px-1.5 rounded-lg">
                          {t('pricing.discountLabel')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {[1, 2, 3, 4].map((i) => (
                    <li key={i} className="flex items-start">
                      <svg className="h-6 w-6 text-secondary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="ml-3 text-primary">{t(`pricing.premiumPlan.feature${i}`)}</span>
                    </li>
                  ))}
                </ul>
                
                <a 
                  href="https://https://forms.gle/yfzs4ENsg2JwPLQPA" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block w-full py-3 px-8 rounded-full bg-dark-gray text-primary font-medium border border-secondary hover:bg-secondary hover:text-background transition-colors duration-300 text-center"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'click_dicci_la_tua', {
                        'event_category': 'engagement',
                        'event_label': 'pricing_premium_plan',
                        'language': document.documentElement.lang || 'it',
                        'page_location': window.location.pathname
                      });
                      console.log('GA4 Event Tracked:', 'click_dicci_la_tua', {
                        label: 'pricing_premium_plan',
                        language: document.documentElement.lang || 'it',
                        page: window.location.pathname
                      });
                    }
                  }}
                >
                  {t('common.shareYourFeedback')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
